import { useState, useEffect } from 'react';
import { Users, Mail, Calendar } from 'lucide-react';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { API } from '@/lib/api';

export default function AdminPage() {
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/contact`),
      ]);
      setLeads(leadsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div data-testid="admin-page" className="pt-20 min-h-screen bg-slate-50">
      <Toaster position="top-center" />
      <div className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-navy mb-2 font-heading">
              Admin Dashboard
            </h1>
            <p className="text-slate-600">Manage demo requests and contact inquiries</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
                <Users className="h-4 w-4 text-emerald" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{leads.length}</div>
                <p className="text-xs text-slate-500">Demo requests</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Contact Messages</CardTitle>
                <Mail className="h-4 w-4 text-emerald" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{contacts.length}</div>
                <p className="text-xs text-slate-500">Total inquiries</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Inquiries</CardTitle>
                <Calendar className="h-4 w-4 text-emerald" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{leads.length + contacts.length}</div>
                <p className="text-xs text-slate-500">Combined</p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="leads" className="space-y-4">
            <TabsList>
              <TabsTrigger value="leads" data-testid="leads-tab">
                Demo Requests ({leads.length})
              </TabsTrigger>
              <TabsTrigger value="contacts" data-testid="contacts-tab">
                Contact Messages ({contacts.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="leads" data-testid="leads-table">
              <Card>
                <CardHeader>
                  <CardTitle>Demo Requests</CardTitle>
                  <CardDescription>
                    All demo requests from potential customers
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-center py-8 text-slate-500">Loading...</p>
                  ) : leads.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">
                      No demo requests yet
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>School Name</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Requirement</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leads.map((lead) => (
                            <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                              <TableCell className="font-medium">{lead.name}</TableCell>
                              <TableCell>{lead.school_name}</TableCell>
                              <TableCell>{lead.phone}</TableCell>
                              <TableCell>{lead.email}</TableCell>
                              <TableCell className="max-w-xs truncate">
                                {lead.requirement}
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                {formatDate(lead.created_at)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contacts" data-testid="contacts-table">
              <Card>
                <CardHeader>
                  <CardTitle>Contact Messages</CardTitle>
                  <CardDescription>
                    All messages from the contact form
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-center py-8 text-slate-500">Loading...</p>
                  ) : contacts.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">
                      No contact messages yet
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Message</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {contacts.map((contact) => (
                            <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                              <TableCell className="font-medium">
                                {contact.name}
                              </TableCell>
                              <TableCell>{contact.email}</TableCell>
                              <TableCell>{contact.phone || 'N/A'}</TableCell>
                              <TableCell className="max-w-xs truncate">
                                {contact.message}
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                {formatDate(contact.created_at)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
